import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import countriesService from '../../services/countriesService';

const initialState = {
  countries: [],
  isLoading: false,
  error: null,
  success: false,
};

export const fetchCountries = createAsyncThunk(
  'countries/fetchAll',
  async (_, thunkAPI) => {
    try {
      return await countriesService.getAll();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch countries';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createCountry = createAsyncThunk(
  'countries/create',
  async (data, thunkAPI) => {
    try {
      return await countriesService.create(data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create country';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateCountry = createAsyncThunk(
  'countries/update',
  async ({ id, data }, thunkAPI) => {
    try {
      return await countriesService.update(id, data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update country';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteCountry = createAsyncThunk(
  'countries/delete',
  async (id, thunkAPI) => {
    try {
      await countriesService.delete(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete country';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const countriesSlice = createSlice({
  name: 'countries',
  initialState,
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCountries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.countries = action.payload;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createCountry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createCountry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.countries.push(action.payload);
        state.success = true;
      })
      .addCase(createCountry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateCountry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCountry.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.countries.findIndex((c) => c._id === action.payload._id);
        if (index !== -1) {
          state.countries[index] = action.payload;
        }
        state.success = true;
      })
      .addCase(updateCountry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteCountry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteCountry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.countries = state.countries.filter((c) => c._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteCountry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess, clearError } = countriesSlice.actions;
export default countriesSlice.reducer;
